import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [loadMoreButton] - The manual load more button.
 * @property {HTMLElement} [shownCount] - The number of currently rendered products.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  #isLoadingMore = false;

  /** @type {MutationObserver | undefined} */
  #filterUpdateObserver;

  /** @type {number | undefined} */
  #filterUpdateTimeout;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.addEventListener('click', this.#handleLoadMore);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.removeEventListener('click', this.#handleLoadMore);
    this.#filterUpdateObserver?.disconnect();
    if (this.#filterUpdateTimeout) window.clearTimeout(this.#filterUpdateTimeout);
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage(updateHistory = true) {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    const existingProductIds = new Set(
      Array.from(grid.querySelectorAll('[data-product-id]'), (card) => card.getAttribute('data-product-id'))
    );
    const uniqueNextPageItems = Array.from(nextPageItemElements).filter((card) => {
      const productId = card.getAttribute('data-product-id');
      return !productId || !existingProductIds.has(productId);
    });

    if (uniqueNextPageItems.length === 0) return;

    grid.append(...uniqueNextPageItems);

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    if (updateHistory) history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  #handleLoadMore = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[ref="loadMoreButton"]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || this.#isLoadingMore) return;

    const label = button.querySelector('[data-load-more-label]');
    const defaultLabel = button.dataset.defaultLabel || label?.textContent || 'Load More';

    this.#isLoadingMore = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = button.dataset.loadingLabel || 'Loading…';

    try {
      await this.#renderNextPage(false);
    } finally {
      this.#isLoadingMore = false;
      button.removeAttribute('aria-busy');
      if (label) label.textContent = defaultLabel;
      this.#updateLoadMoreControls();
    }
  };

  #updateLoadMoreControls() {
    const { grid } = this.refs;
    const button = this.querySelector('[ref="loadMoreButton"]');
    const shownCount = this.querySelector('[ref="shownCount"]');

    if (!grid || !(button instanceof HTMLButtonElement)) return;

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const totalProducts = Number(button.dataset.totalProducts);
    const lastPage = Number(grid.dataset.lastPage);
    const currentPage = Number(cards[cards.length - 1]?.getAttribute('data-page'));

    if (shownCount) shownCount.textContent = String(Math.min(cards.length, totalProducts));

    const isComplete = cards.length >= totalProducts || currentPage >= lastPage;
    button.hidden = isComplete;
    button.disabled = isComplete || this.#isLoadingMore;
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes.
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // Only keep one short-lived observer during rapid filter/sort updates.
    this.#filterUpdateObserver?.disconnect();
    if (this.#filterUpdateTimeout) window.clearTimeout(this.#filterUpdateTimeout);

    this.#filterUpdateObserver = new MutationObserver((mutations) => {
      const newLastPage = this.refs.grid?.dataset.lastPage;
      const gridContentChanged = mutations.some((mutation) => mutation.type === 'childList');

      if (newLastPage !== currentLastPage || gridContentChanged) {
        this.#filterUpdateObserver?.disconnect();
        if (this.#filterUpdateTimeout) window.clearTimeout(this.#filterUpdateTimeout);
        this.#filterUpdateObserver = undefined;
        this.#filterUpdateTimeout = undefined;

        if (!this.isConnected) return;

        this.#observeViewMore();
        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;
    if (grid) {
      this.#filterUpdateObserver.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      this.#filterUpdateTimeout = window.setTimeout(() => {
        this.#filterUpdateObserver?.disconnect();
        this.#filterUpdateObserver = undefined;
        this.#filterUpdateTimeout = undefined;
      }, 3000);
    }
  };
}
